import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Box,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { MetaPredictionsResponse } from '../../../../../../../api/services/mediaPlanning/getMetaPredictionsValidity';
import BookingStatusTable from '../BookingStatusTable/BookingStatusTable';
import { MetaCampaignData } from '../../../../../../../api/services/mediaPlanning/bookMeta';

export interface ExtendedMetaPredictionsResponse
  extends MetaPredictionsResponse {
  rfMain: {
    booked: boolean;
    isGoldenRuleMet: boolean;
  };
  rf1P: {
    booked: boolean;
    isGoldenRuleMet: boolean;
  };
}

export interface PricingAccordionProps {
  pricingData?: MetaCampaignData[];
  isLoading: boolean | undefined;
  isValidityCheck: boolean;
}

function SuccessAccordions({
  pricingData,
  isLoading,
  isValidityCheck,
}: PricingAccordionProps) {
  if (isLoading || !isValidityCheck || !pricingData) return null;

  return (
    <Box>
      {pricingData?.map((country, index) => (
        <Accordion
          key={country.value?.country}
          sx={
            index === 0
              ? {
                  borderTop: '1px solid #c7d8f9',
                  borderBottom: '1px solid #c7d8f9',
                  margin: '0 !important',
                }
              : {
                  borderBottom: '1px solid #c7d8f9',
                  margin: '0 !important',
                }
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              height: '56px',
              background: '#fafafa',
              flexDirection: 'row-reverse',
              '.MuiAccordionSummary-content': {
                height: 'unset',
                pl: 0,
                display: 'flex',
                justifyContent: 'space-between',
              },
            }}
          >
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Stack flexDirection="row" alignItems="center">
                <Typography
                  fontWeight={700}
                  fontSize="14px"
                  textTransform="uppercase"
                  ml={1}
                >
                  {country.value?.country}
                </Typography>
                {country.value &&
                  (country.value.status === 'reserved' ||
                    country.value.status === 'partially-reserved') &&
                  !country.value?.isGoldenRuleMet && (
                    <Tooltip title="Meta golden rules not met">
                      <WarningIcon
                        sx={{ color: '#F00F00', ml: 1, fontSize: '18px' }}
                      />
                    </Tooltip>
                  )}
                {country.value?.status !== 'reserved' && (
                  <Tooltip
                    title={country.value?.message || 'Meta booking failed'}
                  >
                    <WarningIcon
                      sx={{ color: '#F00F00', ml: 1, fontSize: '18px' }}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography fontSize="12px" lineHeight={2}>
                  RF Main:
                </Typography>
                <Stack sx={{ flexDirection: 'row' }}>
                  {country.value?.status === 'reserved' ||
                  country.value?.status === 'partially-reserved' ? (
                    <DoneIcon sx={{ color: '#439A37' }} />
                  ) : (
                    <CloseIcon sx={{ color: '#F00F00' }} />
                  )}
                </Stack>
                {country.value?.reservedPredictionData?.partialPrediction !==
                null ? (
                  <>
                    <Typography fontSize="12px" lineHeight={2}>
                      RF 1P:
                    </Typography>
                    <Stack>
                      {country.value?.status === 'reserved' ? (
                        <DoneIcon sx={{ color: '#439A37' }} />
                      ) : (
                        <CloseIcon sx={{ color: '#F00F00' }} />
                      )}
                    </Stack>
                  </>
                ) : (
                  <Box width="67px" />
                )}
              </Stack>
            </Stack>
          </AccordionSummary>
          {(country?.value?.status === 'reserved' ||
            country?.value?.status === 'partially-reserved') && (
            <AccordionDetails>
              {country?.value && <BookingStatusTable pricingData={country} />}
            </AccordionDetails>
          )}
        </Accordion>
      ))}
    </Box>
  );
}

export default SuccessAccordions;
